import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SearchBar } from '../components/SearchBar';
import { ProductCard } from '../components/ProductCard';
import { ArrowLeft, Loader } from 'lucide-react';
import { API_ENDPOINTS, fetchWithTimeout } from '../config/api';
import { useAuth } from '../context/AuthContext';

interface ProductVariation {
  variant_id: string;
  variant_name: string;
  variant_purchasing_price: string;
  variant_refilling_price: string;
  discount_refilling_price: string;
  discount_purchasing_price: string;
  variant_image: string;
  variant_quantity: string;
}

interface Product {
  product_id: string;
  product_name: string;
  product_description: string;
  product_image: string;
  delivery_fee: number;
  brand_id: string;
  variations: ProductVariation[];
}

interface BrandInfo {
  brand_id: string;
  brand_name: string;
  brand_description: string;
  brand_image: string;
}

interface ApiResponse {
  success: boolean;
  code: number;
  message: string;
  data: {
    products: Product[];
    brand_info: BrandInfo;
  };
}

export const BrandProducts: React.FC = () => {
  const { brandId, brandName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [brandInfo, setBrandInfo] = useState<BrandInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const formData = new URLSearchParams();
        formData.append('brand_id', brandId || '');
        formData.append('user_id', user?.user_id || '');

        const response = await fetchWithTimeout(API_ENDPOINTS.GET_PRODUCTS_BY_BRAND, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          mode: 'cors',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ApiResponse = await response.json();
        
        if (data.success) {
          // Remove duplicate variations
          const uniqueProducts = data.data.products.map(product => ({
            ...product,
            variations: product.variations.filter((variation, index, self) =>
              index === self.findIndex(v => v.variant_id === variation.variant_id)
            )
          }));
          setProducts(uniqueProducts);
          setBrandInfo(data.data.brand_info);
        } else {
          setError(data.message || 'Failed to fetch products');
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (brandId && user?.user_id) {
      setIsLoading(true);
      setProducts([]);
      setBrandInfo(null);
      setError(null);
      fetchProducts();
    }
  }, [brandId, user?.user_id]);

  const filteredProducts = products.filter(product =>
    product.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getVariationsBySize = (variations: ProductVariation[], size: string) => {
    const filtered = variations.filter(v => {
      const variantName = v.variant_name.toLowerCase();
      const sizeNumber = size.split('kg')[0];
      const sizeVariations = [
        size.toLowerCase(),
        `${sizeNumber} kg`,
        `${sizeNumber} Kg`,
        `${sizeNumber}kg`,
        `${sizeNumber} KG`,
        `${sizeNumber}KG`,
      ];
      
      return sizeVariations.some(sizeVariation => 
        variantName.includes(sizeVariation)
      );
    });

    return filtered;
  };

  const renderProductGrid = () => {
    const allCards: React.ReactNode[] = [];

    filteredProducts.forEach(product => {
      const variations6kg = getVariationsBySize(product.variations, '6kg');
      const variations13kg = getVariationsBySize(product.variations, '13kg');

      variations6kg.forEach(variation => {
        allCards.push(
          <ProductCard
            key={`${variation.variant_id}-6kg-new`}
            product={{
              id: `${product.product_id}-${variation.variant_id}-6kg-new`,
              brand: brandInfo?.brand_name || brandName || '',
              image: variation.variant_image || product.product_image,
              description: product.product_description,
              prices: {
                '6kg': {
                  new: parseFloat(variation.variant_purchasing_price),
                  refill: parseFloat(variation.variant_refilling_price)
                },
                '13kg': {
                  new: 0,
                  refill: 0
                }
              }
            }}
            type="new"
            size="6kg"
          />
        );

        allCards.push(
          <ProductCard
            key={`${variation.variant_id}-6kg-refill`}
            product={{
              id: `${product.product_id}-${variation.variant_id}-6kg-refill`,
              brand: brandInfo?.brand_name || brandName || '',
              image: variation.variant_image || product.product_image,
              description: product.product_description,
              prices: {
                '6kg': {
                  new: parseFloat(variation.variant_purchasing_price),
                  refill: parseFloat(variation.variant_refilling_price)
                },
                '13kg': {
                  new: 0,
                  refill: 0
                }
              }
            }}
            type="refill"
            size="6kg"
          />
        );
      });

      variations13kg.forEach(variation => {
        allCards.push(
          <ProductCard
            key={`${variation.variant_id}-13kg-new`}
            product={{
              id: `${product.product_id}-${variation.variant_id}-13kg-new`,
              brand: brandInfo?.brand_name || brandName || '',
              image: variation.variant_image || product.product_image,
              description: product.product_description,
              prices: {
                '6kg': {
                  new: 0,
                  refill: 0
                },
                '13kg': {
                  new: parseFloat(variation.variant_purchasing_price),
                  refill: parseFloat(variation.variant_refilling_price)
                }
              }
            }}
            type="new"
            size="13kg"
          />
        );

        allCards.push(
          <ProductCard
            key={`${variation.variant_id}-13kg-refill`}
            product={{
              id: `${product.product_id}-${variation.variant_id}-13kg-refill`,
              brand: brandInfo?.brand_name || brandName || '',
              image: variation.variant_image || product.product_image,
              description: product.product_description,
              prices: {
                '6kg': {
                  new: 0,
                  refill: 0
                },
                '13kg': {
                  new: parseFloat(variation.variant_purchasing_price),
                  refill: parseFloat(variation.variant_refilling_price)
                }
              }
            }}
            type="refill"
            size="13kg"
          />
        );
      });
    });

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {allCards}
      </div>
    );
  };

  return (
    <div className="pt-16 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 md:mb-0">
            {brandInfo?.brand_name || brandName} Products
          </h1>
          <div className="w-full md:w-96">
            <SearchBar value={searchTerm} onChange={setSearchTerm} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-green-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">
              No products found matching your search criteria.
            </p>
          </div>
        ) : (
          renderProductGrid()
        )}
      </div>
    </div>
  );
};